import { ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { cn } from "~/lib/utils";

import type { ReactNode } from "react";

/**
 * The notice shown when the anti-bot check bounces a submit.
 *
 * Deliberately NOT styled as an error. Nine times out of ten this fires because the
 * challenge had not finished when the person hit the button — they did nothing wrong,
 * and painting it red trains them to think the form is broken. It uses the `gate` tone
 * (amber, "pending"), the same one the product already uses for "not yet available".
 *
 * As a rule it carries no button. The form's own submit button is the action; a second CTA
 * inside the notice would put two competing buttons a few pixels apart. The countdown
 * lives here, the button reads it and re-enables itself.
 *
 * La excepción es `action`: cuando el nivel con clic del antibot no pudo completar, el pedido
 * explícito es un "Reintentar" junto al mensaje (en el paso del código, el "Reenviar" del
 * formulario puede estar en su propia espera de 60 s). Sin `totalSeconds` no hay barra.
 */

interface VerificationNoticeProps {
  title: string;
  body: string;
  /** Seconds still to wait; 0 means the form can be submitted again. */
  secondsLeft?: number;
  /** What `secondsLeft` started from, so the progress bar knows how far along it is. */
  totalSeconds?: number;
  /** Un único botón propio del aviso (ver arriba). */
  action?: ReactNode;
  className?: string;
}

export function VerificationNotice({
  title,
  body,
  secondsLeft = 0,
  totalSeconds = 0,
  action,
  className,
}: VerificationNoticeProps) {
  const elapsed = totalSeconds > 0 ? (totalSeconds - secondsLeft) / totalSeconds : 1;

  return (
    <div
      role="status"
      // polite, not assertive: this interrupts nothing, it explains a pause.
      aria-live="polite"
      className={cn(
        "grid gap-2.5 rounded-[10px] border border-landing-gate/35 bg-landing-gate-bg px-4 py-3.5",
        className,
      )}
    >
      <div className="flex items-start gap-2.5">
        <ShieldCheck aria-hidden="true" className="mt-px size-[18px] shrink-0 text-landing-gate" />
        <div className="grid gap-1">
          <p className="font-display text-[14.5px] font-bold leading-snug text-landing-text">
            {title}
          </p>
          <p className="text-[13px] leading-relaxed text-landing-muted">{body}</p>
        </div>
      </div>

      {/* A bar that visibly fills is the difference between "waiting" and "frozen". */}
      {totalSeconds > 0 && (
        <div
          aria-hidden="true"
          className="h-[3px] overflow-hidden rounded-full bg-landing-gate/15"
          // The countdown ticks once a second, so the bar animates between ticks to
          // avoid a staircase.
        >
          <div
            className="h-full rounded-full bg-landing-gate transition-[width] duration-1000 ease-linear"
            style={{ width: `${Math.min(100, Math.max(0, elapsed * 100))}%` }}
          />
        </div>
      )}

      {action}
    </div>
  );
}

/**
 * Counts down the retry window. The server tells us how long to wait (it owns the number,
 * because it owns the mechanism); this just ticks it to zero.
 */
export function useRetryCountdown() {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [totalSeconds, setTotalSeconds] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stop = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
  }, []);

  const start = useCallback(
    (seconds: number) => {
      stop();
      setTotalSeconds(seconds);
      setSecondsLeft(seconds);
      intervalRef.current = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            stop();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    },
    [stop],
  );

  const clear = useCallback(() => {
    stop();
    setSecondsLeft(0);
    setTotalSeconds(0);
  }, [stop]);

  useEffect(() => stop, [stop]);

  // Memoised for the same reason as useTurnstile's: callers keep it in dep arrays.
  return useMemo(
    () => ({ secondsLeft, totalSeconds, start, clear }),
    [secondsLeft, totalSeconds, start, clear],
  );
}
